/**
 * Ошибка обращения к API Space.
 *
 * - `unauthorized` — нет сессии (HTTP 401 или error_code 100);
 * - `backend` — бэкенд отказал (JSMessage с result: "error" или HTTP 4xx/5xx);
 * - `bad_response` — ответ пришёл, но это не тот JSON, который ждали;
 * - `network` — запрос не дошёл до сервера.
 */
export type SpaceApiErrorKind = 'unauthorized' | 'backend' | 'bad_response' | 'network'

/** Код бэкенда «нужна авторизация» (SpaceException::ACCOUNT_NEED_AUTH). */
export const NEED_AUTH_CODE = 100

export class SpaceApiError extends Error {
  readonly kind: SpaceApiErrorKind
  readonly status?: number
  /** error_code из JSMessage. Имя совпадает с тем, что уже читает Gonka. */
  readonly errorCode?: number
  /** Ошибки по полям формы: error_message или error_details объектом. */
  readonly fieldErrors: Record<string, string>
  /** Разобранное тело ответа, если оно было. */
  readonly data?: unknown

  constructor(
    kind: SpaceApiErrorKind,
    message: string,
    extra: { status?: number; errorCode?: number; fieldErrors?: Record<string, string>; data?: unknown; cause?: unknown } = {},
  ) {
    super(message)
    this.name = 'SpaceApiError'
    this.kind = kind
    this.status = extra.status
    this.errorCode = extra.errorCode
    this.fieldErrors = extra.fieldErrors ?? {}
    this.data = extra.data
    if (extra.cause !== undefined) (this as { cause?: unknown }).cause = extra.cause
  }
}

interface JsMessage {
  error_code?: unknown
  error_description?: unknown
  error_details?: unknown
  error_message?: unknown
  user_message?: unknown
}

const asFieldErrors = (value: unknown): Record<string, string> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const result: Record<string, string> = {}
  for (const [key, message] of Object.entries(value)) result[key] = String(message)
  return result
}

/** Ошибка из тела JSMessage. `status` — HTTP-статус ответа. */
export function errorFromJsMessage(body: unknown, status: number): SpaceApiError {
  const data = (body && typeof body === 'object' ? body : {}) as JsMessage
  const errorCode = typeof data.error_code === 'number' ? data.error_code : undefined
  const fieldErrors = { ...asFieldErrors(data.error_details), ...asFieldErrors(data.error_message) }
  const message =
    [data.user_message, data.error_message, data.error_description].find(
      (value): value is string => typeof value === 'string' && value !== '',
    ) ?? Object.values(fieldErrors)[0] ?? `HTTP ${status}`
  const kind = status === 401 || errorCode === NEED_AUTH_CODE ? 'unauthorized' : 'backend'
  return new SpaceApiError(kind, message, { status, errorCode, fieldErrors, data: body })
}
